import { Badge } from "@/components/ui/badge";
import {
  Phone,
  MapPin,
  Wrench,
  Zap,
  Droplet,
  Hammer,
  Stethoscope,
  ShoppingBag,
  HardHat,
  Sprout,
  Truck,
  GraduationCap,
  type LucideIcon,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import type { Service } from "@shared/schema";

interface ServicesSectionProps {
  services?: Service[];
  isLoading: boolean;
}

/** Keys must match `categoryOptions` in services-manager (case-insensitive). */
const categoryIcons: Record<string, LucideIcon> = {
  plumbing: Droplet,
  electrical: Zap,
  carpentry: Hammer,
  general: Wrench,
  healthcare: Stethoscope,
  retail: ShoppingBag,
  transportation: Truck,
  education: GraduationCap,
  agriculture: Sprout,
  /** Common misspelling in legacy data */
  aggreculture: Sprout,
  construction: HardHat,
  maintenance: Wrench,
};

function iconForServiceCategory(category: string): LucideIcon {
  const key = category.trim().toLowerCase();
  return categoryIcons[key] ?? Wrench;
}

export function ServicesSection({ services, isLoading }: ServicesSectionProps) {
  if (isLoading) {
    return (
      <section className="py-12" data-testid="section-services">
        <div className="text-center mb-8">
          <Skeleton className="h-10 w-64 mx-auto mb-2 rounded-xl neo-card-sm" />
          <Skeleton className="h-4 w-96 mx-auto rounded-xl neo-card-sm" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="p-6 rounded-2xl bg-card neo-card">
              <div className="flex items-start gap-4">
                <Skeleton className="w-12 h-12 rounded-xl neo-icon-box" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-6 w-32 rounded-xl neo-card-sm" />
                  <Skeleton className="h-4 w-20 rounded-xl neo-card-sm" />
                  <Skeleton className="h-4 w-full rounded-xl neo-card-sm" />
                  <Skeleton className="h-4 w-full rounded-xl neo-card-sm" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (!services || services.length === 0) {
    return null;
  }

  return (
    <section className="py-12" data-testid="section-services">
      <div className="text-center mb-8">
        <h2 className="font-poppins text-3xl md:text-4xl font-bold text-foreground mb-2" data-testid="heading-services">
          Essential Services
        </h2>
        <p className="font-inter text-muted-foreground text-lg" data-testid="text-services-subtitle">
          Local service providers ready to help
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {services.map((service) => {
          const Icon = iconForServiceCategory(service.category);
          return (
            <div
              key={service.id}
              className="p-6 rounded-2xl bg-card neo-card transition-all duration-300"
              data-testid={`card-service-${service.id}`}
            >
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-background neo-icon-box flex-shrink-0">
                  <Icon className="w-6 h-6 text-secondary" data-testid={`icon-${service.category}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-poppins text-lg font-bold text-foreground mb-1" data-testid={`name-service-${service.id}`}>
                    {service.name}
                  </h3>
                  <Badge variant="secondary" className="mb-3" data-testid={`category-service-${service.id}`}>
                    {service.category}
                  </Badge>

                  {service.description && (
                    <p className="font-inter text-sm text-muted-foreground mb-3" data-testid={`description-service-${service.id}`}>
                      {service.description}
                    </p>
                  )}

                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-foreground" data-testid={`phone-service-${service.id}`}>
                      <Phone className="w-4 h-4 text-secondary flex-shrink-0" />
                      <span className="font-inter">{service.phone}</span>
                    </div>
                    {service.address && (
                      <div className="flex items-start gap-2 text-sm text-foreground" data-testid={`address-service-${service.id}`}>
                        <MapPin className="w-4 h-4 text-secondary flex-shrink-0 mt-0.5" />
                        <span className="font-inter">{service.address}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
